import { useMemo } from 'react';
import { WeekStart } from '@/i18n/weekStart';
import type { DayjsWithTZ } from '@/utils/dayjs';

export interface ProgressType {
    year: number;
    month: number;
    week: number;
}

const clampProgress = (value: number): number => {
    if (Number.isNaN(value)) return 0;
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
};

const calculateProgressRatio = (elapsed: number, total: number, isCountdown: boolean): number => {
    if (total <= 0) {
        return isCountdown ? 0 : 100;
    }
    const boundedElapsed = Math.min(Math.max(elapsed, 0), total);
    if (isCountdown) {
        return clampProgress(100 - Math.round((boundedElapsed / total) * 100));
    }
    const numerator = Math.min(boundedElapsed + 1, total);
    return clampProgress(Math.round((numerator / total) * 100));
};

export const useTimeProgress = (
    now: DayjsWithTZ,
    isCountdown: boolean,
    weekStartsOn: WeekStart = 'mon',
): ProgressType => {
    return useMemo(() => {
        const startOfDay = now.startOf('day');

        const startOfYear = now.startOf('year');
        const endOfYear = startOfYear.add(1, 'year');
        const daysInYear = endOfYear.diff(startOfYear, 'day') || 1;
        const daysPassedYear = startOfDay.diff(startOfYear, 'day');
        const yearProgress = calculateProgressRatio(daysPassedYear, daysInYear, isCountdown);

        const startOfMonth = now.startOf('month');
        const daysInMonth = now.daysInMonth() || 1;
        const daysPassedMonth = startOfDay.diff(startOfMonth, 'day');
        const monthProgress = calculateProgressRatio(daysPassedMonth, daysInMonth, isCountdown);

        const dayOfWeek = now.day();
        const weekDayIndex = weekStartsOn === 'sun' ? dayOfWeek : (dayOfWeek + 6) % 7;
        const weekProgress = calculateProgressRatio(weekDayIndex, 7, isCountdown);

        return {
            year: yearProgress,
            month: monthProgress,
            week: weekProgress,
        };
    }, [now, isCountdown, weekStartsOn]);
};
